import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { freight } from "../lib/freightApi.js";
import { StaffShell, Panel, Button, Input, Note, Tag, T, STAGE_LABEL, errText, fmtDate } from "./ui.jsx";

/**
 * Our admin: every carrier, and adding one (portal plan slice 6; the schematic's Carriers board).
 *
 * Adding a carrier looks it up with FMCSA and stores the answer, puts it in setup (it calls nobody),
 * and gives it no members: nobody signs in until we add its people from the setup email's answers.
 * The setup email itself is sent from the carrier's own page, after a preview.
 */

const ORDER = ["setup", "rehearsal", "live", "paused"];

export function AddCarrier({ onAdded }) {
  const [f, setF] = useState({ name: "", mcNumber: "", dotNumber: "", contactName: "", contactEmail: "" });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const set = (k) => (v) => setF((x) => ({ ...x, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setMsg(null);
    try {
      const c = await freight.staff.addCarrier(f);
      setF({ name: "", mcNumber: "", dotNumber: "", contactName: "", contactEmail: "" });
      onAdded(c);
    } catch (err) { setMsg({ ok: false, text: errText(err) }); }
    finally { setBusy(false); }
  };
  return (
    <Panel id="add-title" title="Add a carrier" note="We look it up with FMCSA, keep what it says, and start it in setup. It calls nobody and nobody signs in until you move it on.">
      <form onSubmit={submit} style={{ display: "grid", gap: 12 }}>
        <div style={{ display: "grid", gap: 12, gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))" }}>
          <Input label="Carrier name" value={f.name} onChange={set("name")} autoComplete="organization" />
          {/* Two fields, not one: a USDOT sent as an MC can match another carrier, and FMCSA would vouch for that one. */}
          <Input label="MC number" value={f.mcNumber} onChange={set("mcNumber")} placeholder="MC-123456" />
          <Input label="USDOT (if no MC)" value={f.dotNumber} onChange={set("dotNumber")} placeholder="1234567" />
          <Input label="Primary contact" value={f.contactName} onChange={set("contactName")} autoComplete="name" />
          <Input label="Contact email" type="email" value={f.contactEmail} onChange={set("contactEmail")} autoComplete="email" />
        </div>
        <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
          <Button type="submit" disabled={busy || !f.name.trim() || !(f.mcNumber.trim() || f.dotNumber.trim()) || !f.contactEmail.trim()}>{busy ? "Adding…" : "Add the carrier"}</Button>
          <span style={{ fontSize: 12.5, color: T.ink3 }}>Then preview and send the setup email from its page.</span>
        </div>
        <Note msg={msg} />
      </form>
    </Panel>
  );
}

export function CarrierRow({ c }) {
  const lookup = c.fmcsa ? (c.fmcsaReal ? "FMCSA: found" : "FMCSA: not confirmed") : "FMCSA: not looked up";
  const people = c.members === 1 ? "1 person" : `${c.members ?? 0} people`;
  const drivers = c.drivers === 1 ? "1 driver" : `${c.drivers ?? 0} drivers`;
  return (
    <li className="stf-row" style={{ display: "flex", gap: "6px 16px", alignItems: "center", flexWrap: "wrap", padding: "14px 0", borderTop: `1px solid ${T.hairSoft}` }}>
      <span style={{ flex: "1 1 240px", minWidth: 0 }}>
        <span style={{ display: "block", fontSize: 15, fontWeight: 600 }}>{c.name}</span>
        <span style={{ display: "block", fontSize: 12.5, color: T.ink3, marginTop: 2 }}>
          {[c.mcNumber ? `MC ${c.mcNumber}` : null, c.dotNumber ? `USDOT ${c.dotNumber}` : null].filter(Boolean).join(" · ") || "No docket on file"}
          {c.demo ? " · demonstration" : ""}
        </span>
      </span>
      <span style={{ flex: "1 1 260px", minWidth: 0, fontSize: 13, color: T.ink2 }}>
        {[people, drivers, c.staffCreatedBy ? lookup : "Set itself up"].join(" · ")}
        {c.stageAt ? <span style={{ display: "block", color: T.ink3, fontSize: 12.5 }}>{STAGE_LABEL[c.stage] || c.stage} since {fmtDate(c.stageAt)}</span> : null}
      </span>
      <Link to={`/staff/carriers/${c.id}`} className="stf-link" aria-label={`Open ${c.name}`} style={{ fontSize: 13.5, fontWeight: 500, color: T.ink }}>Open ›</Link>
    </li>
  );
}

export function CarrierGroups({ carriers }) {
  return ORDER.map((stage) => {
    const list = carriers.filter((c) => c.stage === stage);
    if (!list.length) return null;
    return (
      <Panel key={stage} id={`group-${stage}`} title={`${STAGE_LABEL[stage]} (${list.length})`} right={stage === "live" ? null : <Tag>{STAGE_LABEL[stage]}</Tag>}>
        <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>{list.map((c) => <CarrierRow key={c.id} c={c} />)}</ul>
      </Panel>
    );
  });
}

export default function StaffCarriers({ initial = null }) {
  const nav = useNavigate();
  const [carriers, setCarriers] = useState(initial);
  const [err, setErr] = useState(null);
  useEffect(() => {
    if (initial) return undefined;
    let alive = true;
    freight.staff.listCarriers().then((c) => alive && setCarriers(c)).catch((e) => alive && setErr(errText(e)));
    return () => { alive = false; };
  }, [initial]);
  return (
    <StaffShell active="carriers">
      <div>
        <h1 style={{ margin: 0, fontSize: 26, fontWeight: 600, letterSpacing: -0.7 }}>Carriers</h1>
        <div style={{ fontSize: 13.5, color: T.ink2, marginTop: 6 }}>Every carrier on Alfred, and where each one is.</div>
      </div>
      <AddCarrier onAdded={(c) => nav(`/staff/carriers/${c.id}`)} />
      <Note msg={err ? { ok: false, text: err } : null} />
      {!carriers && !err && <div style={{ color: T.ink3, fontSize: 13.5 }}>Loading…</div>}
      {carriers && !carriers.length && <div style={{ color: T.ink3, fontSize: 13.5 }}>No carriers yet.</div>}
      {carriers && <CarrierGroups carriers={carriers} />}
    </StaffShell>
  );
}
